import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { Box, Button, Grid, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { enIN } from 'date-fns/locale';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import LeadStore from '../../store/LeadStore';

const CustomButton = styled(Button)({
  backgroundColor: '#FF7959',
  color: '#fff',
  '&:hover': {
    backgroundColor: '#f1785a',
  },
  width: 'fit-content',
});

export const Step2 = observer(({ selectedPipeline, handleStepComplete }) => {
  const [name, setName] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [budget, setBudget] = useState('');
  const [requirement, setRequirement] = useState('');
  const [leadSource, setLeadSource] = useState('');
  const [expectedStartDate, setExpectedStartDate] = useState(null);

  const [nameError, setNameError] = useState(false);
  const [contactNumberError, setContactNumberError] = useState(false);
  const [contactNumberErrorMessage, setContactNumberErrorMessage] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();

    const isNameError = isEmpty(name);
    const isContactNumberValid = contactNumber.length > 0 && contactNumber.length < 10;
    isContactNumberValid && setContactNumberErrorMessage('Please enter valid 10 digit contact number');

    setNameError(isNameError);
    setContactNumberError(isContactNumberValid || contactNumberError);

    if (isNameError || isContactNumberValid || contactNumberError) {
      return;
    }

    handleStepComplete({
      name,
      contactNo: contactNumber,
      amount: budget === '' ? null : budget,
      requirement,
      leadSource,
      date: expectedStartDate,
    });
  };

  const isEmpty = (value) => {
    return value === '' || value === null || value === undefined;
  };

  const handleContactNumberInputChange = (e) => {
    let { value } = e.target;
    value = value.replace(/e/gi, '');
    if (/^\d{0,10}$/.test(value)) {
      setContactNumberError(false);
      setContactNumber(value);
      LeadStore.checkLeadExists(selectedPipeline.id, value);
    }
  };

  const handleBudgetInputChange = (e) => {
    let { value } = e.target;
    value = value.replace(/e/gi, '');
    if (/^\d*\.?\d{0,2}$/.test(value)) {
      setBudget(value);
    }
  };

  const handleNameInputChange = (e) => {
    let { value } = e.target;
    setName(value);
    setNameError(isEmpty(value));
  };

  useEffect(() => {
    setContactNumberExistError();
  }, [LeadStore.isLeadExist]);

  const setContactNumberExistError = () => {
    setContactNumberError(LeadStore.isLeadExist);
    setContactNumberErrorMessage('Lead already exist with this contact number. Please enter another one.');
  };

  return (
    <>
      <Stack direction={'column'} spacing={2}>
        <div id="step1-header">Name your first lead</div>
        <p id="step-1-p1">
          Deal stages are the steps in your pipeline. They mark the real-life milestones in your sales process.
        </p>
        <p id="step-1-p2">Start with renaming them as you like. You can also add, remove or rename stages later.</p>
      </Stack>
      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
        <Stack direction={'column'} spacing={2}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={12} md={12} lg={6}>
              <Stack spacing={2}>
                <TextField
                  id="outlined-name-flexible"
                  name="outlined-name-flexible"
                  label="Name"
                  size="small"
                  type="text"
                  onChange={handleNameInputChange}
                  value={name}
                  error={nameError}
                  autoFocus
                  required
                />
                <TextField
                  id="outlined-contact-number-flexible"
                  name="outlined-contact-number-flexible"
                  label="Contact Number"
                  type="number"
                  onChange={handleContactNumberInputChange}
                  onKeyDown={(e) => {
                    if (e.key.toLocaleLowerCase() === 'e') {
                      e.preventDefault();
                    }
                  }}
                  value={contactNumber}
                  error={contactNumberError}
                  helperText={contactNumberError ? contactNumberErrorMessage : ''}
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    maxLength: 0,
                    maxLength: 10,
                  }}
                  size="small"
                />
                <TextField
                  id="outlined-requirments-flexible"
                  name="outlined-requirments-flexible"
                  label="Requirments"
                  onChange={(e) => setRequirement(e.target.value)}
                  type="text"
                  size="small"
                  value={requirement}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={6}>
              <Stack spacing={2}>
                <TextField
                  id="outlined-leadSource-flexible"
                  name="outlined-leadSource-flexible"
                  label="Lead Source"
                  onChange={(e) => setLeadSource(e.target.value)}
                  type="text"
                  size="small"
                  value={leadSource}
                />
                <TextField
                  id="outlined-budget-flexible"
                  name="outlined-budget-flexible"
                  label="Budget (If Any)"
                  type="number"
                  onChange={handleBudgetInputChange}
                  onKeyDown={(e) => {
                    if (e.key.toLocaleLowerCase() === 'e') {
                      e.preventDefault();
                    }
                  }}
                  value={budget}
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    maxLength: 0,
                    maxLength: 10,
                  }}
                  size="small"
                />
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIN}>
                  <DatePicker
                    label="Expected start date"
                    value={expectedStartDate ? new Date(expectedStartDate) : null}
                    onChange={(newValue) => {
                      setExpectedStartDate(newValue);
                    }}
                    slotProps={{ textField: { size: 'small' } }}
                  />
                </LocalizationProvider>
              </Stack>
            </Grid>
          </Grid>
          <CustomButton variant="contained" type="submit">
            Next : Closing stages
          </CustomButton>
        </Stack>
      </Box>
    </>
  );
});
